import {
  faCalendar,
  faCalendarPlus,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faHeart,
  faLocationDot,
  faMap,
  faMapPin,
  faNoteSticky,
  faPartyBell,
  faPen,
  faPlus,
  faRoute,
  faTrash,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  parseAsFloat,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs';
import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTripContext } from '@/contexts/TripContext';
import { PlacePhotosCorner } from '@/design-system/composite/PlacePhotosCorner';
import { TripPhotosCorner } from '@/design-system/composite/TripPhotosCorner';
import { Button } from '@/design-system/primitives/Button';
import { CategoryPin } from '@/design-system/primitives/CategoryPin';
import { ChipButton } from '@/design-system/primitives/ChipButton';
import { CollapsibleSection } from '@/design-system/primitives/CollapsibleSection';
import { IconButton } from '@/design-system/primitives/IconButton';
import { InlineEmptyState } from '@/design-system/primitives/InlineEmptyState';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import { TextArea, TextInput } from '@/design-system/primitives/TextInput';
import { TickCounter } from '@/design-system/primitives/TickCounter';
import { useWeather } from '@/hooks/useWeather';
import type { ItineraryItem, RegularHours } from '@/types/itinerary';
import {
  type EnRouteDirection,
  type Trip,
  type TripPlace,
  type TripPlaceType,
  tripPlaceTypes,
} from '@/types/trip';
import { formatAddress } from '@/utils/address';
import { categoryDefinitions } from '@/utils/categories';
import { haversineDistanceMiles } from '@/utils/distance';
import {
  computeOpenStatus,
  dateForDay,
  formatDateRange,
  formatDayCount,
  formatDayLabel,
  formatDurationMinutes,
  formatRegularTime,
  formatScheduledTime,
  hoursForDate,
  lodgingBoundaryForDay,
  lodgingForDay,
  lodgingTimelineTime,
  type OpenStatus,
  tripDayCount,
} from '@/utils/schedule';
import { twx } from '@/utils/tailwind';

export interface TripPanelProps {
  onClose: () => void;
  /** When true, hide trip-meta editing (date range edit, delete trip).
   * Used in Travel mode so the user can't accidentally rescope or
   * destroy a trip while on the road. */
  lockTripMeta?: boolean;
  /** When true, hide the schedule button on each place row so items
   * can't be rescheduled. Notes and en-route edits stay available. */
  lockScheduling?: boolean;
}

// Modes in which the timeline rows surface photo upload/remove. Read
// inside TripPlaceRow via nuqs so each row stays self-sufficient (the
// parent doesn't need to thread a `canEditPhotos` prop down through
// LogisticsGroup and other intermediates). Photos still RENDER in any
// mode when they exist — only the upload/remove affordances are gated.
const photoEditModes = ['explore', 'scout', 'curate', 'travel'] as const;
const photoEditModeParser = parseAsStringLiteral(photoEditModes);

/** Direction of the most recent day change. Used to pick which directional
 * slide animation to play on the day-content wrapper. `init` means the
 * component just mounted (no previous day to compare against). */
type DayChangeDirection = 'init' | 'next' | 'prev';

/** Tracks active-day transitions so the day-content slide can mimic an iOS
 * push/pop (slide in from right on next, from left on prev). Returns
 * `init` on the first render after mount. */
const useDayChangeDirection = (activeDay: number): DayChangeDirection => {
  const prevRef = useRef(activeDay);
  const direction: DayChangeDirection =
    prevRef.current === activeDay ? 'init' : activeDay > prevRef.current ? 'next' : 'prev';
  useEffect(() => {
    prevRef.current = activeDay;
  }, [activeDay]);
  return direction;
};

// Active day for the trip pager — backed by the `day` URL param so it can be linked between
// the trip panel, planning view, and travel mode map. Returns 1 when no day is set in the URL.
const useActiveDay = (): [number, (next: number | ((prev: number) => number)) => void] => {
  const [raw, setRaw] = useQueryState('day', parseAsInteger);
  const active = raw ?? 1;
  const setActive = (next: number | ((prev: number) => number)) => {
    if (typeof next === 'function') {
      void setRaw((prev) => next(prev ?? 1));
    } else {
      void setRaw(next);
    }
  };
  return [active, setActive];
};

// ─── Constants ───────────────────────────────────────────────────────────────

const placeTypeLabels: Record<TripPlaceType, string> = {
  lodging: 'Lodging',
  activity: 'Activity',
  convenience: 'Logistics',
};

// Colors mirror the map's trip place rings so the same itinerary type reads the same
// across map markers, badges, and section headers.
const placeTypeBadgeClasses: Record<TripPlaceType, string> = {
  activity: 'bg-secondary-subtle text-secondary-focal border border-secondary-main',
  lodging: 'bg-tertiary-subtle text-tertiary-emphasized border border-tertiary-main',
  convenience: 'bg-quaternary-subtle text-quaternary-emphasized border border-quaternary-main',
};

const today = (): string => new Date().toISOString().slice(0, 10);

/** Returns the id of the activity the user is "in" right now on a given
 * day, if today matches that day's date. Picks the most recently started
 * scheduled activity (the one whose start time is the latest among those
 * already started by `nowMinutes`). Returns null when the viewed day
 * isn't today, or when no activity has a scheduledTime <= now. */
const getCurrentActivityId = (trip: Trip, day: number, nowMinutes: number): string | null => {
  // Build today's date in the same `YYYY-MM-DD` format dateForDay returns.
  const t = new Date();
  const todayIso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  if (dateForDay(trip, day) !== todayIso) return null;

  let bestId: string | null = null;
  let bestStart = -1;
  for (const p of trip.places) {
    if (p.scheduledDay !== day || !p.scheduledTime) continue;
    const [h, m] = p.scheduledTime.split(':').map(Number);
    const start = (h ?? 0) * 60 + (m ?? 0);
    if (start <= nowMinutes && start > bestStart) {
      bestStart = start;
      bestId = p.item.id;
    }
  }
  return bestId;
};

/** Minute-resolution clock for "what's happening now" UI. Returns minutes
 * since midnight; ticks every 60s so consumers re-render once a minute. */
const minutesSinceMidnight = (d: Date): number => d.getHours() * 60 + d.getMinutes();
const useNowMinutes = (): number => {
  const [now, setNow] = useState(() => minutesSinceMidnight(new Date()));
  useEffect(() => {
    const interval = setInterval(() => setNow(minutesSinceMidnight(new Date())), 60_000);
    return () => clearInterval(interval);
  }, []);
  return now;
};

const formatDate = (ts: number): string =>
  new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** Effective time a TripPlace occupies in a day's timeline. Activity uses its
 * scheduledTime; lodging on its check-in / check-out day uses the configured
 * boundary time. Returns null when the place doesn't anchor to a time. */
const timelineTimeFor = (p: TripPlace, day: number): string | null => {
  if (p.placeType === 'activity') return p.scheduledTime ?? null;
  if (p.placeType === 'lodging') return lodgingTimelineTime(p, day);
  return null;
};

/** Build the day's timeline rows: every activity scheduled on `day`, plus any
 * lodging whose check-in or check-out falls on `day` (and has a time set).
 * Returns rows sorted chronologically — untimed activities sink to the bottom
 * (existing behavior). Lodging never sinks to the bottom because it only
 * surfaces here when it has a boundary time. */
const buildDayTimeline = (places: Array<TripPlace>, day: number): Array<TripPlace> => {
  const rows: Array<TripPlace> = [];
  for (const p of places) {
    if (p.placeType === 'activity') {
      rows.push(p);
      continue;
    }
    if (p.placeType === 'lodging' && timelineTimeFor(p, day) != null) {
      rows.push(p);
    }
  }
  return rows.sort((a, b) => {
    const aTime = timelineTimeFor(a, day);
    const bTime = timelineTimeFor(b, day);
    if (!(aTime || bTime)) return 0;
    if (!aTime) return 1;
    if (!bTime) return -1;
    return aTime.localeCompare(bTime);
  });
};

const parseHHMMToMinutes = (time: string): number | null => {
  const [h, m] = time.split(':').map(Number);
  if (h == null || Number.isNaN(h) || m == null || Number.isNaN(m)) return null;
  return h * 60 + m;
};

/** Minutes of unscheduled time between two consecutive timeline rows on
 * `day`. Returns null when either row has no anchored time. When the
 * previous row has no `estimatedDurationMinutes` set we fall back to a
 * duration of 0 — most users don't enter durations and we still want to
 * surface the start-to-start span as "free time" between activities.
 * Lodging entries are anchored at their boundary time and always treated
 * as instantaneous (check-in / check-out don't consume the day). */
const timelineGapMinutes = (prev: TripPlace, next: TripPlace, day: number): number | null => {
  const prevTime = timelineTimeFor(prev, day);
  const nextTime = timelineTimeFor(next, day);
  if (!(prevTime && nextTime)) return null;
  const prevStart = parseHHMMToMinutes(prevTime);
  const nextStart = parseHHMMToMinutes(nextTime);
  if (prevStart == null || nextStart == null) return null;
  const prevDuration = prev.placeType === 'lodging' ? 0 : (prev.estimatedDurationMinutes ?? 0);
  return nextStart - (prevStart + prevDuration);
};

/** Compact "free time" branch off the day's vertical timeline spine. A
 * small dot anchors onto the spine, a horizontal line extends from there
 * toward the right edge of the card (fading out before it touches the
 * border), and a pill with the duration label centers along the line
 * (its bg-white covers the line behind it). The row dividers immediately
 * above and below are forced to a much paler color (content-banding) via
 * `!` important — `divide-y` selects via the parent so a regular class
 * wouldn't outrank it. Sits between two consecutive timeline rows
 * whenever there's any positive unscheduled time between them. */
const TimelineGapIndicator = ({ minutes }: { minutes: number }) => {
  const label = formatDurationMinutes(minutes);
  if (!label) return null;
  return (
    <div
      className="relative h-6 pointer-events-none !border-t-content-banding [&+*]:!border-t-content-banding"
      aria-hidden
      data-testid="timeline-gap-indicator"
    >
      {/* Horizontal branch — meets the vertical spine at the dot's center
          (left-[5rem]) and runs toward the right inset of the card. The
          mask-image fades the rightmost ~25% so the line dissolves into
          the card edge instead of stopping abruptly. */}
      <div
        className="absolute left-[5rem] right-3 top-1/2 -translate-y-1/2 h-px bg-secondary-emphasized/30"
        style={{
          maskImage: 'linear-gradient(to right, black 0%, black 75%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, black 0%, black 75%, transparent 100%)',
        }}
      />
      {/* Branch dot. w-3 puts it at 12px — clearly smaller than the 24px
          activity pins (CategoryPin sm = w-6) while staying visible. */}
      <span className="absolute left-[5rem] top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-secondary-emphasized/70" />
      {/* Duration badge — centered along the horizontal branch. The
          bg-white covers the branch line behind it, so the line reads as
          one piece passing under the badge. */}
      <div className="absolute left-[5rem] right-3 top-1/2 -translate-y-1/2 flex justify-center">
        <span className="inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[10px] leading-none font-medium text-content-subtle bg-white border border-secondary-emphasized/30 whitespace-nowrap">
          {label} free
        </span>
      </div>
    </div>
  );
};

// ─── Distance helpers ─────────────────────────────────────────────────────────

interface DistanceInfo {
  miles: number;
  label: string;
}

const getDistanceInfo = (tripPlace: TripPlace, trip: Trip): DistanceInfo | null => {
  const { item, scheduledDay, placeType } = tripPlace;

  // If scheduled and there's a lodging on that day (and this isn't the lodging), use lodging distance
  if (scheduledDay != null && placeType !== 'lodging') {
    const dayLodging = lodgingForDay(trip.places, scheduledDay);
    if (dayLodging) {
      const miles = haversineDistanceMiles(
        dayLodging.item.latitude,
        dayLodging.item.longitude,
        item.latitude,
        item.longitude,
      );
      return { miles, label: 'from lodging' };
    }
  }

  // Fall back to trip's lat/lng
  if (trip.lat != null && trip.lng != null) {
    const miles = haversineDistanceMiles(trip.lat, trip.lng, item.latitude, item.longitude);
    return { miles, label: `from ${formatAddress(trip.location) || 'trip'}` };
  }

  return null;
};

// ─── Hours helpers ────────────────────────────────────────────────────────────

interface HoursInfo {
  hours: RegularHours | null;
  isToday: boolean;
  isOpenNow?: boolean;
}

const getHoursInfo = (item: ItineraryItem, trip: Trip, scheduledDay: number): HoursInfo => {
  const isoDate = dateForDay(trip, scheduledDay);
  const todayStr = today();
  const isToday = isoDate === todayStr;
  const hours = hoursForDate(item.hours?.regular, isoDate);
  return { hours, isToday, isOpenNow: isToday ? item.hours?.isOpenNow : undefined };
};

// ─── ScheduleForm ─────────────────────────────────────────────────────────────

interface ScheduleFormSave {
  scheduledDay: number;
  scheduledDayEnd: number | null;
  scheduledTime: string | null;
  estimatedDurationMinutes: number | null;
  checkInTime: string | null;
  checkInEndTime: string | null;
  checkOutTime: string | null;
  checkOutEndTime: string | null;
}

interface ScheduleFormProps {
  tripPlace: TripPlace;
  trip: Trip;
  onSave: (next: ScheduleFormSave) => void;
  onUnschedule?: () => void;
  onCancel: () => void;
}

/** Clamp a number to [0, max]; safe-coerces NaN → 0. Used for the hours/min
 * duration inputs so users can type freely without invalid intermediate
 * states leaking into state. */
const clampDurationField = (raw: string, max: number): number => {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, max);
};

const ScheduleForm = ({ tripPlace, trip, onSave, onUnschedule, onCancel }: ScheduleFormProps) => {
  const dayCount = tripDayCount(trip);
  const isLodgingType = tripPlace.placeType === 'lodging';
  // Lodging defaults its range to the entire trip (day 1 → last day) so the
  // common-case stay just needs check-in/out times. Activities default to
  // day 1.
  const [selectedDay, setSelectedDay] = useState(tripPlace.scheduledDay ?? 1);
  const [selectedDayEnd, setSelectedDayEnd] = useState(
    tripPlace.scheduledDayEnd ?? (isLodgingType ? dayCount : (tripPlace.scheduledDay ?? 1)),
  );
  const [selectedTime, setSelectedTime] = useState(tripPlace.scheduledTime ?? '');

  // Duration is stored as a single minutes value but edited as two fields so
  // it's clear at a glance ("2h 15m" instead of "135 min"). Defaults to 0h/0m
  // when unset — the data-source midpoint was too presumptuous; user prefers
  // an empty starting state.
  const initialMinutes = tripPlace.estimatedDurationMinutes ?? 0;
  const [durationHours, setDurationHours] = useState(String(Math.floor(initialMinutes / 60)));
  const [durationMinutes, setDurationMinutes] = useState(String(initialMinutes % 60));

  // Lodging times. Check-in is a window (earliest–latest the host will take
  // you); check-out is a single deadline you have to be out by, so it gets one
  // field rather than a range.
  const [checkInStart, setCheckInStart] = useState(tripPlace.checkInTime ?? '');
  const [checkInEnd, setCheckInEnd] = useState(tripPlace.checkInEndTime ?? '');
  const [checkOutStart, setCheckOutStart] = useState(tripPlace.checkOutTime ?? '');

  const isActivity = tripPlace.placeType === 'activity';
  const isLodging = tripPlace.placeType === 'lodging';
  const isMultiDay = isLodging || tripPlace.placeType === 'convenience';

  // All days covered by other lodging entries (to disable as unavailable for lodging only)
  const takenLodgingDays = new Set<number>();
  if (isLodging) {
    for (const p of trip.places) {
      if (p.placeType !== 'lodging' || p.item.id === tripPlace.item.id || p.scheduledDay == null) {
        continue;
      }
      const end = p.scheduledDayEnd ?? p.scheduledDay;
      for (let d = p.scheduledDay; d <= end; d++) takenLodgingDays.add(d);
    }
  }

  const startLabel = isLodging ? 'Check-in day' : 'From day';
  const endLabel = isLodging ? 'Check-out day' : 'To day';

  const handleStartDayChange = (day: number) => {
    setSelectedDay(day);
    // Clamp end to be at least the new start day
    if (selectedDayEnd < day) setSelectedDayEnd(day);
  };

  // Brief "saved!" flash on the Schedule button before the form closes.
  // Pure visual feedback — the actual save happens synchronously via
  // onSave; the flash is the user-visible confirmation.
  const [justSaved, setJustSaved] = useState(false);
  const handleSave = () => {
    const h = clampDurationField(durationHours, 999);
    const m = clampDurationField(durationMinutes, 59);
    const totalMinutes = h * 60 + m;
    setJustSaved(true);
    onSave({
      scheduledDay: selectedDay,
      scheduledDayEnd: isMultiDay ? selectedDayEnd : null,
      scheduledTime: isActivity && selectedTime ? selectedTime : null,
      // Activity-only. Forcing null for other types retires durations stored
      // back when the field was offered to every type.
      estimatedDurationMinutes: isActivity && totalMinutes > 0 ? totalMinutes : null,
      checkInTime: isLodging && checkInStart ? checkInStart : null,
      checkInEndTime: isLodging && checkInEnd ? checkInEnd : null,
      checkOutTime: isLodging && checkOutStart ? checkOutStart : null,
      // Always cleared: check-out is a deadline, not a window. Writing null
      // retires any value stored before that distinction existed.
      checkOutEndTime: null,
    });
  };

  // Build the per-day option list once — reused by all four select widgets
  // below. Each option carries a "taken" hint when another lodging entry has
  // already claimed it.
  const dayOptions = Array.from({ length: dayCount }, (_, i) => i + 1);

  return (
    <div className="mt-2 p-2.5 bg-content-focal rounded-xl border border-content-border space-y-3">
      {isMultiDay ? (
        // Lodging / convenience: two grouped sections — Check-in then
        // Check-out — each with day + (lodging-only) time range stacked. No
        // grid columns at the outer level, so the form never overflows on
        // narrow panels.
        <>
          <div className="space-y-1.5">
            <SectionEyebrow>{startLabel.replace(' day', '')}</SectionEyebrow>
            <div>
              <span className="text-[10px] text-content-subtle block mb-0.5">Day</span>
              <select
                value={selectedDay}
                onChange={(e) => handleStartDayChange(Number(e.target.value))}
                className="w-full text-xs px-2 py-1.5 border border-content-border rounded-lg bg-white"
              >
                {dayOptions.map((day) => {
                  const disabled = takenLodgingDays.has(day);
                  return (
                    <option key={day} value={day} disabled={disabled}>
                      Day {day} — {formatDayLabel(dateForDay(trip, day))}
                      {disabled ? ' (taken)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
            {isLodging && (
              <div>
                <span className="text-[10px] text-content-subtle block mb-0.5">
                  Time range (optional)
                </span>
                <div className="flex items-center gap-1.5">
                  <TextInput
                    type="time"
                    value={checkInStart}
                    onChange={(e) => setCheckInStart(e.target.value)}
                    className="min-w-0 flex-1"
                  />
                  <span className="text-xs text-content-disabled">–</span>
                  <TextInput
                    type="time"
                    value={checkInEnd}
                    onChange={(e) => setCheckInEnd(e.target.value)}
                    className="min-w-0 flex-1"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <SectionEyebrow>{endLabel.replace(' day', '')}</SectionEyebrow>
            <div>
              <span className="text-[10px] text-content-subtle block mb-0.5">Day</span>
              <select
                value={selectedDayEnd}
                onChange={(e) => setSelectedDayEnd(Number(e.target.value))}
                className="w-full text-xs px-2 py-1.5 border border-content-border rounded-lg bg-white"
              >
                {dayOptions
                  .filter((day) => day >= selectedDay)
                  .map((day) => {
                    const disabled = takenLodgingDays.has(day);
                    return (
                      <option key={day} value={day} disabled={disabled}>
                        Day {day} — {formatDayLabel(dateForDay(trip, day))}
                        {disabled ? ' (taken)' : ''}
                      </option>
                    );
                  })}
              </select>
            </div>
            {isLodging && (
              <div>
                <span className="text-[10px] text-content-subtle block mb-0.5">
                  Time (optional)
                </span>
                <TextInput
                  type="time"
                  value={checkOutStart}
                  onChange={(e) => setCheckOutStart(e.target.value)}
                  className="min-w-0 w-full"
                  aria-label="Check-out time"
                />
              </div>
            )}
          </div>
        </>
      ) : (
        // Activity / single-day: day + optional time on one row
        <div className="flex flex-wrap gap-2 items-start">
          <div className="flex-1 min-w-0">
            <span className="text-xs text-content-subtle block mb-0.5">Day</span>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(Number(e.target.value))}
              className="w-full text-xs px-2 py-1.5 border border-content-border rounded-lg bg-white"
            >
              {dayOptions.map((day) => (
                <option key={day} value={day}>
                  Day {day} — {formatDayLabel(dateForDay(trip, day))}
                </option>
              ))}
            </select>
          </div>
          {isActivity && (
            <div className="min-w-[100px]">
              <span className="text-xs text-content-subtle block mb-0.5">Time (optional)</span>
              <TextInput
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {/* Duration is activity-only: you don't "spend 2h" at a hotel or a gas
          stop. Lodging durations were already thrown away downstream (the
          timeline gap math hardcodes 0 for lodging), and multi-day logistics
          spans are bounded by their day range, not a duration. Empty / zero
          clears the field. */}
      {isActivity && (
        <div>
          <span className="text-xs text-content-subtle block mb-0.5">Duration (optional)</span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-content-subtle">
              <TextInput
                type="number"
                min={0}
                max={999}
                step={1}
                value={durationHours}
                onChange={(e) => setDurationHours(e.target.value)}
                className="w-14"
                aria-label="Duration hours"
              />
              <span>hours</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-content-subtle">
              <TextInput
                type="number"
                min={0}
                max={59}
                step={5}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                className="w-14"
                aria-label="Duration minutes"
              />
              <span>min</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        <Button
          onClick={handleSave}
          fullWidth
          className={twx(
            'flex-1 min-w-[80px]',
            // Quick green flash + outline ring when the user hits Save —
            // the form usually unmounts before the animation completes,
            // but the user sees the affirming pulse.
            justSaved && 'motion-safe:animate-kawaii-flash ring-2 ring-success-main/60',
          )}
        >
          Schedule
        </Button>
        {onUnschedule && (
          <Button variant="destructive-ghost" onClick={onUnschedule} className="whitespace-nowrap">
            Unschedule
          </Button>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

// ─── TripPlaceRow ─────────────────────────────────────────────────────────────

interface TripPlaceRowProps {
  tripPlace: TripPlace;
  trip: Trip;
  onNavigate: (item: ItineraryItem) => void;
  onRemove: (placeId: string) => void;
  onSchedule: (placeId: string, update: ScheduleFormSave) => void;
  onUnschedule: (placeId: string) => void;
  /** Render in timeline format: time on the left, pin sitting on a vertical
   * line, icon styled solid instead of duotone for clarity. Used inside the
   * Curate-mode day-by-day activities list. */
  timeline?: boolean;
  /** Day this row is being rendered for in the timeline. Used to look up
   * which lodging boundary (check-in vs check-out) anchors a lodging entry to
   * this day's timeline. Only meaningful when `timeline` is true. */
  timelineDay?: number;
  /** If true, mark this row as the activity currently happening based on
   * wall-clock time. Renders a colored left rail + subtle row tint so
   * Travel-mode users can see "this is where I should be right now." */
  isCurrent?: boolean;
  /** When true, hides the schedule button so items can't be re-slotted onto
   * a different day. Notes + remove still work. */
  lockScheduling?: boolean;
}

const TripPlaceRowImpl = ({
  tripPlace,
  trip,
  onNavigate,
  onRemove,
  onSchedule,
  onUnschedule,
  timeline = false,
  timelineDay,
  isCurrent = false,
  lockScheduling = false,
}: TripPlaceRowProps) => {
  const { updatePlaceNote, updatePlaceType } = useTripContext();
  // Mode controls whether the photos UI is editable on this row.
  // Photos still display in any mode when they exist — only the
  // upload/remove affordances are gated.
  const [mode] = useQueryState('mode', photoEditModeParser.withDefault('explore'));
  const canEditPhotos = mode === 'curate' || mode === 'travel';
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [noteValue, setNoteValue] = useState(tripPlace.note ?? '');
  const nowMinutes = useNowMinutes();
  const { item, placeType, scheduledDay, scheduledTime, estimatedDurationMinutes } = tripPlace;
  const def = categoryDefinitions[item.appCategory];
  const distanceInfo = getDistanceInfo(tripPlace, trip);
  const hoursInfo = scheduledDay != null ? getHoursInfo(item, trip, scheduledDay) : null;
  const durationLabel = formatDurationMinutes(estimatedDurationMinutes);
  const appleMapsUrl = `https://maps.apple.com/?ll=${item.latitude},${item.longitude}&q=${encodeURIComponent(item.name)}`;

  // Lodging within a day-scoped list (either the timeline view or the day
  // pager's Activities section) reads its time from the configured check-in /
  // check-out range, not scheduledTime. The boundary tells us which range
  // anchors this row, and we surface a "Check-in"/"Check-out" label + the
  // full range under the title.
  const lodgingBoundary =
    placeType === 'lodging' && timelineDay != null
      ? lodgingBoundaryForDay(tripPlace, timelineDay)
      : null;
  const lodgingRangeStart =
    lodgingBoundary === 'check-in'
      ? tripPlace.checkInTime
      : lodgingBoundary === 'check-out'
        ? tripPlace.checkOutTime
        : null;
  // Check-in only. Check-out is a single deadline, so its row renders one time
  // and falls through to the `{start}` branch of the label below.
  const lodgingRangeEnd = lodgingBoundary === 'check-in' ? tripPlace.checkInEndTime : null;
  // Effective time shown in the timeline's left column.
  const timelineDisplayTime = lodgingBoundary
    ? (lodgingRangeStart ?? null)
    : (scheduledTime ?? null);

  // Today's open / closing-soon / closed indicator, derived from the place's
  // regular hours and the current wall-clock time. The dot always reflects
  // *today's* status regardless of when the place is scheduled — "is it open
  // right now if I called?" is a useful at-a-glance signal whether the place
  // is on today's plan or three days out. Null → unknown → gray dot.
  const todayHours = hoursForDate(item.hours?.regular, today());
  const liveOpenStatus = item.hours?.regular ? computeOpenStatus(todayHours, nowMinutes) : null;

  // Open/closed line. Always render when we know anything about the place's
  // hours (scheduled date hours, today's regular hours, or just the binary
  // isOpenNow flag — even just "Hours unavailable" reads as useful structure).
  const showHoursLine = true;
  const hoursRangeLabel: string | null = (() => {
    if (hoursInfo?.hours) {
      const h = hoursInfo.hours;
      return h.open === '0000' && h.close === '2400'
        ? '24 hours'
        : `${formatRegularTime(h.open)} – ${formatRegularTime(h.close)}`;
    }
    if (todayHours) {
      return todayHours.open === '0000' && todayHours.close === '2400'
        ? '24 hours'
        : `${formatRegularTime(todayHours.open)} – ${formatRegularTime(todayHours.close)}`;
    }
    return null;
  })();
  // Indicator status drives the colored dot. We only ever use today's
  // regular-hours-driven status — never the API's `isOpenNow`, which is
  // captured at fetch time and quickly goes stale for places scheduled days
  // in the future. `null` here means "unknown" and renders as a gray dot.
  const indicatorStatus: OpenStatus | null = liveOpenStatus;

  return (
    <div
      className={twx(
        'relative flex items-start gap-2.5 p-2.5 transition-colors',
        isCurrent && 'bg-primary-subtle/60',
      )}
    >
      {/* "Happening now" rail — bright pink bar on the left edge for the
          activity whose start time is the most recently passed. Absolute
          positioning avoids any layout shift on non-current rows. */}
      {isCurrent && (
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary-emphasized" aria-hidden />
      )}
      {/* Timeline mode: scheduled time + duration on the left of the line.
          w-12 (48px) sized for the widest content — "10:30pm" and longer
          durations like "1h 30m" — with a touch more breathing room so the
          duration never wraps to a second line. `whitespace-nowrap` is a
          belt-and-braces guard for unusually long duration strings. */}
      {timeline && (
        <div className="shrink-0 w-12 pt-1 text-right text-xs leading-tight space-y-0.5">
          {timelineDisplayTime && (
            <span className="font-medium text-content-text block whitespace-nowrap">
              {formatScheduledTime(timelineDisplayTime)}
            </span>
          )}
          {durationLabel && (
            <span className="text-content-disabled block whitespace-nowrap">{durationLabel}</span>
          )}
        </div>
      )}
      {/* Category pin. In timeline mode, sit above the vertical line via
          relative+z-10 so the line terminates at the pin. */}
      <CategoryPin
        color={def.pinColor}
        icon={def.icon}
        size="sm"
        solid={timeline}
        className={twx('mt-0.5', timeline && 'relative z-10')}
      />

      <div className="flex-1 min-w-0 space-y-0.5">
        {/* Photo tile floats top-right so the title + metadata lines
            wrap around it. Has to live first in the DOM for the float
            to take effect on subsequent siblings. The corner
            self-suppresses when there's nothing to show AND no edit
            capability. */}
        <PlacePhotosCorner
          item={item}
          trip={trip}
          canEdit={canEditPhotos}
          className="float-right ml-2 mb-1"
        />
        {/* Title row — name on the left, action icons aligned to the title's
            vertical center on the right. items-center means all three icons
            sit on the same baseline as the title text. */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onNavigate(item)}
            className="flex-1 min-w-0 text-sm font-medium text-content-text hover:text-secondary-emphasized transition-colors text-left leading-snug truncate"
          >
            {item.name}
          </button>
          {/* Action icons hidden in view-only contexts (lockScheduling=true,
              i.e. Travel mode), where the row is a read-only schedule entry. */}
          {!lockScheduling && (
            <div className="shrink-0 flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => {
                  setShowNoteForm(!showNoteForm);
                  setNoteValue(tripPlace.note ?? '');
                }}
                title={tripPlace.note ? 'Edit note' : 'Add note'}
                className={twx(
                  'w-7 h-7 rounded-lg flex items-center justify-center transition-colors',
                  showNoteForm || tripPlace.note
                    ? 'text-tertiary-emphasized bg-tertiary-subtle'
                    : 'text-content-subtle hover:text-tertiary-emphasized hover:bg-content-focal',
                )}
                aria-label={tripPlace.note ? 'Edit note' : 'Add note'}
              >
                {/* faNoteSticky is a clean square shape — matches faCalendar's
                    visual weight without the extra pen-overlay complexity that
                    made the previous icon read too big. faXmark is a slim X
                    that needs slightly more pixels to balance the trio. */}
                <FontAwesomeIcon icon={faNoteSticky} style={{ width: 12, height: 12 }} />
              </button>
              <button
                type="button"
                onClick={() => setShowScheduleForm(!showScheduleForm)}
                title={scheduledDay != null ? 'Edit schedule' : 'Schedule'}
                className={twx(
                  'w-7 h-7 rounded-lg flex items-center justify-center transition-colors',
                  showScheduleForm
                    ? 'text-secondary-emphasized bg-secondary-subtle'
                    : 'text-content-subtle hover:text-secondary-emphasized hover:bg-content-focal',
                )}
                aria-label={scheduledDay != null ? 'Edit schedule' : 'Schedule this place'}
              >
                <FontAwesomeIcon icon={faCalendar} style={{ width: 12, height: 12 }} />
              </button>
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-content-disabled hover:text-error-main hover:bg-error-badge transition-colors"
                aria-label={`Remove ${item.name}`}
              >
                <FontAwesomeIcon icon={faXmark} style={{ width: 14, height: 14 }} />
              </button>
            </div>
          )}
        </div>

        {/* Duration under the title — non-timeline rows only. In timeline
            mode the duration sits in the left rail under the time. */}
        {!timeline && durationLabel && (
          <p className="text-xs text-content-subtle">{durationLabel}</p>
        )}

        {/* Type badge + schedule label. The badge is the ONLY way to reach
            "Lodging", so it has to read as a control rather than a label —
            hence the caret (same disclosure vocabulary as TripModeMenu) and
            a hover that adds emphasis instead of fading it away. */}
        <div className="flex flex-wrap items-center gap-1.5 relative pt-0.5">
          <button
            type="button"
            onClick={() => setShowTypeMenu((v) => !v)}
            title="Change itinerary type"
            // A disclosure, not an ARIA menu: the popup holds ordinary buttons
            // and has no roving-focus arrow navigation, so claiming
            // aria-haspopup="menu" would promise keyboard semantics we don't
            // implement. Matches CollapsibleSection / FiltersPopover.
            aria-expanded={showTypeMenu}
            // Leads with the visible label so the accessible name contains it
            // (WCAG 2.5.3 Label in Name) while still saying what the control does.
            aria-label={`${placeTypeLabels[placeType]} — change itinerary type`}
            className={twx(
              'inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[10px] font-medium leading-tight cursor-pointer',
              'transition-shadow hover:ring-2 hover:ring-current/25',
              placeTypeBadgeClasses[placeType],
            )}
          >
            {placeTypeLabels[placeType]}
            <FontAwesomeIcon
              icon={faChevronDown}
              aria-hidden
              style={{ width: 7, height: 7 }}
              className={twx('transition-transform duration-150', showTypeMenu ? 'rotate-180' : '')}
            />
          </button>
          {showTypeMenu && (
            <>
              <button
                type="button"
                aria-label="Close type menu"
                onClick={() => setShowTypeMenu(false)}
                className="fixed inset-0 z-10 cursor-default"
              />
              <div className="absolute top-6 left-0 z-20 bg-white border border-content-border rounded-xl shadow-xl p-1.5 w-44">
                <p className="text-xs font-medium text-content-subtle px-2 py-1">Itinerary type</p>
                {tripPlaceTypes.map((t) => {
                  const isCurrent = t === placeType;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        if (!isCurrent) updatePlaceType(trip.id, item.id, t);
                        setShowTypeMenu(false);
                      }}
                      className={twx(
                        'w-full text-left text-xs px-2 py-1.5 rounded-lg transition-colors flex items-center justify-between',
                        isCurrent
                          ? 'bg-secondary-subtle text-secondary-focal font-medium'
                          : 'text-content-text hover:bg-content-focal',
                      )}
                    >
                      <span>{placeTypeLabels[t]}</span>
                      {isCurrent && <span className="text-xs">✓</span>}
                    </button>
                  );
                })}
                {scheduledDay != null && (
                  <p className="text-xs text-content-disabled italic px-2 pt-1.5 pb-1 leading-tight">
                    Changing the type will unschedule this place.
                  </p>
                )}
              </div>
            </>
          )}
          {/* Within a day-scoped list (timelineDay set) and the row is a
              lodging boundary, swap the day-range label for the more useful
              boundary label + time range. Falls through to the legacy
              "Day N · time" label everywhere else (Logistics, Unscheduled). */}
          {lodgingBoundary && (lodgingRangeStart || lodgingRangeEnd) ? (
            <span className="text-xs text-content-subtle">
              {lodgingBoundary === 'check-in' ? 'Check-in' : 'Check-out'}
              {lodgingRangeStart && (
                <>
                  {' '}
                  <span className="text-base leading-none">·</span>{' '}
                  {formatScheduledTime(lodgingRangeStart)}
                  {lodgingRangeEnd && ` – ${formatScheduledTime(lodgingRangeEnd)}`}
                </>
              )}
            </span>
          ) : (
            !timeline &&
            scheduledDay != null && (
              <span className="text-xs text-content-subtle">
                {tripPlace.scheduledDayEnd != null && tripPlace.scheduledDayEnd !== scheduledDay
                  ? `Days ${scheduledDay}–${tripPlace.scheduledDayEnd}`
                  : `Day ${scheduledDay}`}
                {scheduledTime && (
                  <>
                    {' '}
                    <span className="text-base leading-none">·</span>{' '}
                    {formatScheduledTime(scheduledTime)}
                  </>
                )}
              </span>
            )
          )}
        </div>

        {/* Open hours / address / distance share a 3-column micro-layout: a
            10px leading icon/dot in a fixed-width slot, then the text. The
            slot keeps the three text columns vertically aligned. */}
        {showHoursLine && (
          <div className="flex items-center gap-1.5 text-xs text-content-subtle leading-snug">
            <span className="shrink-0 w-2.5 flex justify-center">
              <span
                className={twx(
                  'w-2 h-2 rounded-full',
                  indicatorStatus === 'open' && 'bg-success-main',
                  indicatorStatus === 'closing-soon' && 'bg-warning-main',
                  indicatorStatus === 'closed' && 'bg-error-main',
                )}
                // Theme overrides --color-* with `initial`, so Tailwind's
                // `bg-gray-*` utilities resolve to nothing. Inline the unknown
                // color (true neutral, not the purple-tinted "disabled" token)
                // so the dot reads as gray regardless of the theme reset.
                style={indicatorStatus == null ? { backgroundColor: '#d1d5db' } : undefined}
                aria-hidden
              />
            </span>
            <span>
              Open: {hoursRangeLabel ?? 'Hours unavailable'}
              {indicatorStatus === 'closing-soon' && (
                <span className="ml-1.5 inline-flex items-center px-1.5 py-px rounded-full text-[10px] font-semibold uppercase tracking-wide bg-warning-badge text-warning-text border border-warning-border leading-none align-middle motion-safe:animate-kawaii-soft-pulse">
                  closing soon
                </span>
              )}
            </span>
          </div>
        )}

        {/* Address → Apple Maps */}
        <a
          href={appleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-1.5 text-xs text-secondary-emphasized hover:underline leading-snug"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="shrink-0 w-2.5 flex justify-center mt-0.5">
            <FontAwesomeIcon icon={faMapPin} style={{ width: 9, height: 9 }} />
          </span>
          <span className="line-clamp-2">{formatAddress(item.address)}</span>
        </a>

        {/* Distance — uniform style with the other two lines so the trio reads
            as a single metadata block. Icon mirrors the route/distance idea
            without leaning on any specific home-base concept. */}
        {distanceInfo && (
          <div className="flex items-center gap-1.5 text-xs text-content-subtle leading-snug">
            <span className="shrink-0 w-2.5 flex justify-center">
              <FontAwesomeIcon icon={faRoute} style={{ width: 9, height: 9 }} />
            </span>
            <span>
              {distanceInfo.miles.toFixed(1)} mi {distanceInfo.label}
            </span>
          </div>
        )}

        {/* Note — non-interactive display. Only the note-edit pencil
            in the action row above opens the editor; hovering/clicking
            the note body was too easy to trigger accidentally. The
            `whitespace-pre-wrap` preserves any newlines the user typed
            in the textarea. */}
        {tripPlace.note && !showNoteForm && (
          <p className="font-handwritten text-[15px] text-content-deemphasized text-left leading-snug w-full pt-1.5 whitespace-pre-wrap">
            {tripPlace.note}
          </p>
        )}
        {showNoteForm && (
          <div className="mt-1 space-y-1">
            <TextArea
              autoFocus={true}
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              placeholder="Add a note…"
              rows={3}
              size="sm"
              className="font-handwritten text-[15px] leading-snug text-content-deemphasized"
            />
            <div className="flex gap-1.5">
              <Button
                size="xs"
                fullWidth
                className="flex-1"
                onClick={() => {
                  updatePlaceNote(trip.id, item.id, noteValue || null);
                  setShowNoteForm(false);
                }}
              >
                Save
              </Button>
              {tripPlace.note && (
                <Button
                  size="xs"
                  variant="destructive-ghost"
                  onClick={() => {
                    updatePlaceNote(trip.id, item.id, null);
                    setNoteValue('');
                    setShowNoteForm(false);
                  }}
                >
                  Remove
                </Button>
              )}
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setNoteValue(tripPlace.note ?? '');
                  setShowNoteForm(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Schedule form */}
        {showScheduleForm && (
          <ScheduleForm
            tripPlace={tripPlace}
            trip={trip}
            onSave={(next) => {
              onSchedule(item.id, next);
              setShowScheduleForm(false);
            }}
            onUnschedule={
              scheduledDay != null
                ? () => {
                    onUnschedule(item.id);
                    setShowScheduleForm(false);
                  }
                : undefined
            }
            onCancel={() => setShowScheduleForm(false)}
          />
        )}
      </div>
    </div>
  );
};

// Memo skips re-renders when the row's own data is unchanged. Note that
// `trip` is the entire Trip object — when any place in the trip is added/
// scheduled/removed, every row will still re-render (the trip reference
// changes). The win is for parent re-renders that don't touch trip data, like
// the day-pager click or unrelated AppInner state cascades.
const TripPlaceRow = memo(TripPlaceRowImpl);

// ─── LogisticsGroup ───────────────────────────────────────────────────────────

export interface LogisticsGroupProps {
  places: Array<TripPlace>;
  trip: Trip;
  onNavigate: (item: ItineraryItem) => void;
  onRemove: (placeId: string) => void;
  onSchedule: (placeId: string, update: ScheduleFormSave) => void;
  onUnschedule: (placeId: string) => void;
  lockScheduling?: boolean;
}

export const LogisticsGroup = ({
  places,
  trip,
  onNavigate,
  onRemove,
  onSchedule,
  onUnschedule,
  lockScheduling = false,
}: LogisticsGroupProps) => {
  if (places.length === 0) return null;

  const lodging = places.find((p) => p.placeType === 'lodging');
  const others = places.length - (lodging ? 1 : 0);
  const collapsedSubtitle = lodging
    ? others > 0
      ? `${lodging.item.name}, and ${others} more`
      : lodging.item.name
    : `${places.length} ${places.length === 1 ? 'place' : 'places'}`;

  return (
    <CollapsibleSection
      title="Logistics"
      count={places.length}
      collapsedSubtitle={collapsedSubtitle}
      defaultOpen={false}
    >
      <div className="divide-y divide-content-divider">
        {places.map((tp) => (
          <TripPlaceRow
            key={tp.item.id}
            tripPlace={tp}
            trip={trip}
            onNavigate={onNavigate}
            onRemove={onRemove}
            onSchedule={onSchedule}
            onUnschedule={onUnschedule}
            lockScheduling={lockScheduling}
          />
        ))}
      </div>
    </CollapsibleSection>
  );
};

// ─── EnRouteSection ──────────────────────────────────────────────────────────

export interface EnRouteSectionProps {
  trip: Trip;
  direction: EnRouteDirection;
  /** View-only mode — hide Add and Edit affordances. Travel mode passes
   * this so its day pager renders a read-only schedule. */
  lockScheduling?: boolean;
}

export const EnRouteSection = ({
  trip,
  direction,
  lockScheduling = false,
}: EnRouteSectionProps) => {
  const { addEnRouteNote, updateEnRouteNote, removeEnRouteNote } = useTripContext();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  // Force-open when the user starts adding so the form is visible.
  const [open, setOpen] = useState(true);
  // Notes without an explicit direction default to 'in' (legacy data was always Day 1)
  const notes = (trip.enRoute ?? []).filter((n) => (n.direction ?? 'in') === direction);

  const headerLabel = direction === 'in' ? 'On the way there' : 'On the way home';
  const emptyHint =
    direction === 'in'
      ? 'Stops, restaurants, or anything interesting on the drive.'
      : 'Last stops, snacks, or detours for the trip home.';
  const placeholder =
    direction === 'in'
      ? 'e.g. Stop at Maple Corner General Store…'
      : 'e.g. Coffee at the cute spot in Burlington…';

  const startEdit = (id: string, text: string) => {
    setEditingId(id);
    setEditText(text);
  };

  const saveEdit = () => {
    if (editingId && editText.trim()) updateEnRouteNote(trip.id, editingId, editText.trim());
    setEditingId(null);
  };

  const saveNew = () => {
    if (newText.trim()) addEnRouteNote(trip.id, newText.trim(), direction);
    setNewText('');
    setIsAdding(false);
  };

  // In view-only contexts (travel) with no notes, suppress the section
  // entirely — there's nothing to show and no action to offer.
  if (lockScheduling && notes.length === 0) return null;

  const collapsedSubtitle = (() => {
    const first = notes[0]?.text;
    if (!first) return null;
    if (notes.length === 1) return first;
    return `${first}, and ${notes.length - 1} more`;
  })();

  return (
    <CollapsibleSection
      title={headerLabel}
      count={notes.length || undefined}
      collapsedSubtitle={collapsedSubtitle ?? undefined}
      open={open}
      onOpenChange={setOpen}
      trailing={
        !lockScheduling ? (
          <button
            type="button"
            onClick={() => {
              setIsAdding(true);
              setOpen(true);
            }}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-content-disabled hover:text-secondary-emphasized hover:bg-secondary-subtle transition-colors"
            aria-label="Add en-route note"
          >
            <FontAwesomeIcon icon={faPlus} style={{ width: 9, height: 9 }} />
            Add
          </button>
        ) : undefined
      }
    >
      <div className="px-3 py-2">
        {notes.length > 0 && (
          <div className="space-y-0.5 mb-1">
            {notes.map((note) => (
              <div key={note.id}>
                {editingId === note.id ? (
                  <div className="space-y-1 py-1">
                    <TextArea
                      autoFocus={true}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      autoGrow
                      className="font-handwritten text-base leading-snug text-content-deemphasized"
                    />
                    <div className="flex gap-1.5">
                      <Button size="xs" fullWidth className="flex-1" onClick={saveEdit}>
                        Save
                      </Button>
                      <Button
                        size="xs"
                        variant="destructive-ghost"
                        onClick={() => {
                          removeEnRouteNote(trip.id, note.id);
                          setEditingId(null);
                        }}
                      >
                        Remove
                      </Button>
                      <Button size="xs" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 py-0.5">
                    <p className="flex-1 font-handwritten text-base text-content-deemphasized leading-snug">
                      {note.text}
                    </p>
                    {!lockScheduling && (
                      <IconButton
                        icon={faPen}
                        ariaLabel="Edit note"
                        size="sm"
                        tone="subtle"
                        onClick={() => startEdit(note.id, note.text)}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {isAdding && !lockScheduling && (
          <div className="space-y-1.5 py-1">
            <TextArea
              autoFocus={true}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder={placeholder}
              rows={2}
              autoGrow
              className="font-handwritten text-base leading-snug text-content-deemphasized"
            />
            <div className="flex gap-1.5">
              <Button size="xs" fullWidth className="flex-1" onClick={saveNew}>
                Add
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setNewText('');
                  setIsAdding(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {notes.length === 0 && !isAdding && !lockScheduling && (
          <p className="text-xs text-content-disabled italic">{emptyHint}</p>
        )}
      </div>
    </CollapsibleSection>
  );
};

// ─── TripDetailView ───────────────────────────────────────────────────────────

interface TripDetailViewProps {
  trip: Trip;
  onClose: () => void;
  lockTripMeta?: boolean;
  lockScheduling?: boolean;
}

const TripDetailView = ({
  trip,
  onClose,
  lockTripMeta = false,
  lockScheduling = false,
}: TripDetailViewProps) => {
  // Wall-clock minute used to highlight the "happening now" activity. Ticks
  // once a minute so the indicator stays in sync without manual refresh.
  const nowMinutes = useNowMinutes();
  const { removePlace, deleteTrip, updatePlaceSchedule, unschedulePlace, updateTripDates } =
    useTripContext();
  const [currentLat, setLat] = useQueryState('lat', parseAsFloat);
  const [currentLng, setLng] = useQueryState('lng', parseAsFloat);
  const [, setRadius] = useQueryState('radius', parseAsInteger.withDefault(10));
  const [, setSelectedItemId] = useQueryState('item', parseAsString);
  // Trip-level photo upload is only meaningful in Travel mode here —
  // this view also renders in Explore via the slide-over TripPanel,
  // where we show photos read-only.
  const [mode] = useQueryState('mode', photoEditModeParser.withDefault('explore'));
  const canEditTripPhotos = mode === 'travel';

  const [activeDay, setActiveDay] = useActiveDay();
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editStartDate, setEditStartDate] = useState(trip.startDate);
  const [editEndDate, setEditEndDate] = useState(trip.endDate);

  // Day-pager scroll container — reset to top on day-change so the new
  // day's content always reads from the start instead of inheriting the
  // previous day's scroll offset. Tracks the previous day in a ref so
  // the effect (a) actually references `activeDay` (biome would
  // otherwise strip the dep as unused under --unsafe) and (b) skips the
  // initial mount.
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const prevActiveDayRef = useRef(activeDay);
  useEffect(() => {
    if (prevActiveDayRef.current === activeDay) return;
    prevActiveDayRef.current = activeDay;
    timelineScrollRef.current?.scrollTo({ top: 0 });
  }, [activeDay]);

  // Fall back to the current map coordinates if the trip itself has no stored location
  const { weather } = useWeather(trip, currentLat, currentLng);
  const weatherByDate = new Map(weather.map((w) => [w.date, w]));

  const dayCount = tripDayCount(trip);
  const dateRange = formatDateRange(trip.startDate, trip.endDate);

  // A place is "scheduled" — and thus eligible to appear in a day timeline —
  // only when it carries enough info to slot itself in. Activities need a
  // day AND a time; lodging/convenience just need a day (their times are
  // captured separately via check-in/out ranges or aren't time-anchored).
  // Anything else lands in the Unscheduled bucket.
  const isScheduled = (p: TripPlace): boolean => {
    if (p.scheduledDay == null) return false;
    if (p.placeType === 'activity') return p.scheduledTime != null;
    return true;
  };
  const unscheduled = trip.places.filter((p) => !isScheduled(p));

  // Group scheduled places by day
  const scheduled = trip.places.filter(isScheduled);
  const byDay = new Map<number, Array<TripPlace>>();
  for (let d = 1; d <= dayCount; d++) {
    byDay.set(d, []);
  }
  for (const p of scheduled) {
    if (p.scheduledDay == null) continue;
    const spansMultipleDays =
      p.scheduledDayEnd != null &&
      p.scheduledDayEnd !== p.scheduledDay &&
      (p.placeType === 'lodging' || p.placeType === 'convenience');
    if (spansMultipleDays && p.scheduledDayEnd != null) {
      for (let d = p.scheduledDay; d <= p.scheduledDayEnd; d++) {
        byDay.get(d)?.push(p);
      }
    } else {
      byDay.get(p.scheduledDay)?.push(p);
    }
  }

  const handleNavigate = useCallback(
    (item: ItineraryItem) => {
      void setLat(item.latitude);
      void setLng(item.longitude);
      void setRadius(5);
      void setSelectedItemId(item.id);
    },
    [setLat, setLng, setRadius, setSelectedItemId],
  );

  const handleRemove = useCallback(
    (placeId: string) => removePlace(trip.id, placeId),
    [removePlace, trip.id],
  );

  const handleSchedule = useCallback(
    (placeId: string, update: ScheduleFormSave) => updatePlaceSchedule(trip.id, placeId, update),
    [updatePlaceSchedule, trip.id],
  );

  const handleUnschedule = useCallback(
    (placeId: string) => unschedulePlace(trip.id, placeId),
    [unschedulePlace, trip.id],
  );

  // Two-tap inline-confirm pattern instead of window.confirm — on iOS
  // the native confirm dialog can leave the slide-over scrim orphaned
  // on the screen after dismiss, blocking all input until reload.
  const [pendingDelete, setPendingDelete] = useState(false);
  const handleDelete = () => {
    if (!pendingDelete) {
      setPendingDelete(true);
      return;
    }
    deleteTrip(trip.id);
    setPendingDelete(false);
    onClose();
  };
  // Reset the confirm state any time the trip context changes (user
  // navigated away, picked a different trip, etc.).
  useEffect(() => {
    setPendingDelete(false);
  }, []);

  const dayDirection = useDayChangeDirection(activeDay);

  return (
    // bg-white on the outer wrapper so the scroll body's `p-4` padding-top
    // region renders white instead of letting the underlying kawaii bg show
    // through above the sticky day pager.
    <div className="flex flex-col h-full bg-white">
      {/* Header — mirrors the Curate left-pane header: title first, then
          calendar/location/place-count meta below it. The back-to-map
          button (small screens only) is a labelled pill so it reads
          unambiguously as "tap me to return to the map", not as a
          decorative icon. */}
      <div className="shrink-0 px-4 py-3 border-b border-content-divider bg-white">
        {!isEditingDates && (
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={onClose}
              aria-label="Back to map"
              className="md:hidden inline-flex items-center gap-1.5 -ml-1 px-2.5 py-1 rounded-full border border-content-border bg-white shadow-sm text-xs font-medium text-content-subtle hover:bg-content-focal hover:text-content-text transition-colors"
            >
              <FontAwesomeIcon icon={faMap} style={{ width: 12, height: 12 }} />
              <span>Map</span>
            </button>
            {!lockTripMeta && (
              <button
                type="button"
                onClick={onClose}
                className="ml-auto w-6 h-6 flex items-center justify-center rounded-full hover:bg-content-focal text-content-subtle hover:text-content-text transition-colors"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faXmark} style={{ width: 12, height: 12 }} />
              </button>
            )}
          </div>
        )}
        {isEditingDates ? (
          // Inline date edit form
          <div className="space-y-2">
            <p className="text-xs font-medium text-content-subtle">Edit trip dates</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-xs text-content-disabled block mb-0.5">Start</span>
                <TextInput
                  type="date"
                  value={editStartDate}
                  onChange={(e) => {
                    setEditStartDate(e.target.value);
                    if (editEndDate < e.target.value) setEditEndDate(e.target.value);
                  }}
                />
              </div>
              <div>
                <span className="text-xs text-content-disabled block mb-0.5">End</span>
                <TextInput
                  type="date"
                  value={editEndDate}
                  min={editStartDate}
                  onChange={(e) => setEditEndDate(e.target.value)}
                />
              </div>
            </div>
            {(() => {
              const newDayCount = Math.max(
                1,
                Math.round(
                  (new Date(`${editEndDate}T00:00:00`).getTime() -
                    new Date(`${editStartDate}T00:00:00`).getTime()) /
                    86_400_000,
                ) + 1,
              );
              return (
                trip.places.some((p) => p.scheduledDay != null && p.scheduledDay > newDayCount) && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                    Some scheduled places fall outside the new date range and will be moved to
                    Unplanned.
                  </p>
                )
              );
            })()}
            <div className="flex gap-1.5">
              <Button
                fullWidth
                className="flex-1"
                onClick={() => {
                  updateTripDates(trip.id, editStartDate, editEndDate);
                  setActiveDay(1);
                  setIsEditingDates(false);
                }}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setEditStartDate(trip.startDate);
                  setEditEndDate(trip.endDate);
                  setIsEditingDates(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          // Flex row: text content on the left (flex-1 min-w-0),
          // trip-photos corner pinned top-right.
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="font-diary text-2xl text-content-text leading-tight">{trip.name}</h2>
              <p className="text-xs text-content-subtle flex items-start gap-1 mt-1 flex-wrap">
                <FontAwesomeIcon
                  icon={faCalendar}
                  style={{ width: 10, height: 10 }}
                  className="mt-0.5 shrink-0"
                />
                <span className="break-words">
                  {dateRange}
                  <span className="text-content-disabled"> · {formatDayCount(trip)}</span>
                </span>
                {!lockTripMeta && (
                  <button
                    type="button"
                    onClick={() => setIsEditingDates(true)}
                    className="ml-0.5 inline-flex items-center p-0.5 rounded text-content-disabled hover:text-content-subtle hover:bg-content-focal transition-colors"
                    aria-label="Edit trip dates"
                    title="Edit trip dates"
                  >
                    <FontAwesomeIcon icon={faPen} style={{ width: 9, height: 9 }} />
                  </button>
                )}
              </p>
              {trip.location && (
                <p className="text-xs text-content-subtle flex items-start gap-1 mt-0.5">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    style={{ width: 10, height: 10 }}
                    className="mt-0.5 shrink-0"
                  />
                  <span>{formatAddress(trip.location)}</span>
                </p>
              )}
              <p className="text-xs text-content-disabled mt-0.5">
                {trip.places.length} {trip.places.length === 1 ? 'place' : 'places'}
                <span className="text-content-disabled/60">
                  {' '}
                  · added {formatDate(trip.createdAt)}
                </span>
              </p>
              {!lockTripMeta && (
                <div className="flex items-center gap-2 mt-2.5">
                  {pendingDelete ? (
                    <div className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-error-background border border-error-border px-2 py-1 shadow-sm">
                      <span className="text-[11px] font-medium text-error-text">Delete trip?</span>
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-error-main text-white hover:bg-error-text transition-colors"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(false)}
                        className="px-2 py-0.5 rounded-full text-[11px] font-medium text-content-subtle hover:text-content-text transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleDelete}
                      aria-label="Delete trip"
                      title="Delete trip"
                      className="w-8 h-8 rounded-full bg-content-focal hover:bg-error-background flex items-center justify-center shadow-sm border border-content-border text-content-subtle hover:text-error-main hover:border-error-border transition-colors ml-auto"
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ width: 12, height: 12 }} />
                    </button>
                  )}
                </div>
              )}
            </div>
            <TripPhotosCorner trip={trip} canEdit={canEditTripPhotos} />
          </div>
        )}
      </div>

      {/* Body */}
      {trip.places.length === 0 ? (
        <InlineEmptyState
          icon={faHeart}
          title="No places yet"
          message="Tap the heart icon on any place to add it to this trip."
        />
      ) : (
        // No `pt-*` on this scroll container: the sticky day-pager's
        // `top: 0` would otherwise stick INSIDE the padding box, leaving
        // a `pt`-tall strip above it where the en-route section (which
        // scrolls underneath) peeks through. The first non-sticky child
        // (the unscheduled wrapper) carries its own `pt-4` so the gap
        // above it isn't lost when unscheduled is rendered.
        <div ref={timelineScrollRef} className="flex-1 overflow-y-auto px-4 pb-4 space-y-5">
          {/* Unscheduled */}
          {unscheduled.length > 0 && (
            <div className="space-y-1 pt-4">
              <div className="flex items-center gap-2 px-1 py-1.5">
                <span className="text-xs font-bold text-content-text uppercase tracking-wider">
                  Unscheduled
                </span>
                <span className="text-xs text-content-disabled">{unscheduled.length}</span>
              </div>
              <div className="divide-y divide-content-divider border border-content-border rounded-xl overflow-hidden">
                {unscheduled.map((tp) => (
                  <TripPlaceRow
                    key={tp.item.id}
                    tripPlace={tp}
                    trip={trip}
                    onNavigate={handleNavigate}
                    onRemove={handleRemove}
                    onSchedule={handleSchedule}
                    onUnschedule={handleUnschedule}
                    lockScheduling={lockScheduling}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Day pager */}
          {dayCount > 0 && (
            <div>
              {/* Pager nav — sticky to the top of the scroll container so it
                  stays visible as the day's content scrolls underneath.
                  Negative horizontal margins + matching padding extend the
                  white background edge-to-edge inside the scroll viewport.
                  `md:min-h-[6.5rem]` matches the left pane's trip-header
                  min-height so the bottom borders of the two panes meet
                  cleanly at the vertical pane divider. `z-20` keeps the
                  sticky bg-white above the day content's own z-10 timeline
                  spine + pins — otherwise the later-in-DOM pins win the
                  z-tie and bleed through the pager. */}
              <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-3 bg-white grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-content-divider md:min-h-[6.5rem] [&>*:first-child]:justify-self-start [&>*:last-child]:justify-self-end">
                <ChipButton
                  tone="paging"
                  size="sm"
                  icon={faChevronLeft}
                  onClick={() => setActiveDay((d) => Math.max(1, d - 1))}
                  disabled={activeDay === 1}
                  aria-label="Previous day"
                  // min-w + justify-start keeps the prev button the
                  // same width whether or not "Day N-1" renders, so
                  // both pager buttons mirror each other and the day
                  // chip stays geometrically centered.
                  className="min-w-[88px] justify-start"
                >
                  {activeDay > 1 && <span>Day {activeDay - 1}</span>}
                </ChipButton>
                <div
                  key={`day-chip-${activeDay}`}
                  className="text-center inline-flex flex-col items-center bg-white border border-content-border rounded-2xl shadow-sm px-3 py-1.5 motion-safe:animate-kawaii-day-chip-tilt"
                >
                  <p className="text-xs font-bold text-content-text uppercase tracking-wider">
                    Day <TickCounter value={activeDay} />
                  </p>
                  <p className="text-xs text-content-subtle">
                    {formatDayLabel(dateForDay(trip, activeDay))}
                  </p>
                  {(() => {
                    const w = weatherByDate.get(dateForDay(trip, activeDay));
                    return w ? (
                      <p className="text-xs text-content-subtle flex items-center justify-center gap-1.5 mt-0.5">
                        <span
                          title={w.condition}
                          className="motion-safe:animate-kawaii-breath inline-block"
                        >
                          {w.conditionEmoji}
                        </span>
                        <span>
                          {w.tempHighF}°/{w.tempLowF}°
                        </span>
                        {w.precipitationProbability > 10 && (
                          <span className="text-info-text">💧{w.precipitationProbability}%</span>
                        )}
                      </p>
                    ) : null;
                  })()}
                </div>
                <ChipButton
                  tone="paging"
                  size="sm"
                  onClick={() => setActiveDay((d) => Math.min(dayCount, d + 1))}
                  disabled={activeDay === dayCount}
                  aria-label="Next day"
                  // Mirror the prev button's min-w + opposite justify so
                  // both pager buttons stay the same width even when
                  // "Day N+1" doesn't render (last day).
                  className="min-w-[88px] justify-end"
                >
                  {activeDay < dayCount && <span>Day {activeDay + 1}</span>}
                  <FontAwesomeIcon icon={faChevronRight} style={{ width: 11, height: 11 }} />
                </ChipButton>
              </div>

              {/* Active day content. Wrapped in a keyed div so each day
                  change replays the swoosh fade-in animation — the `key`
                  remount is what restarts the CSS animation. We pick the
                  directional slide based on whether the user paged
                  forward or backward (iOS-style push/pop). */}
              {(() => {
                const places = byDay.get(activeDay) ?? [];
                const logistics = places.filter(
                  (p) => p.placeType === 'lodging' || p.placeType === 'convenience',
                );
                const activities = buildDayTimeline(places, activeDay);
                const currentActivityId = getCurrentActivityId(trip, activeDay, nowMinutes);
                return (
                  <div
                    key={activeDay}
                    className={twx(
                      'motion-safe:animate-kawaii-day-fade',
                      dayDirection === 'next' && 'motion-safe:animate-kawaii-day-slide-next',
                      dayDirection === 'prev' && 'motion-safe:animate-kawaii-day-slide-prev',
                    )}
                  >
                    {activeDay === 1 && (
                      <EnRouteSection trip={trip} direction="in" lockScheduling={lockScheduling} />
                    )}
                    {activeDay === dayCount && dayCount > 1 && (
                      <EnRouteSection trip={trip} direction="out" lockScheduling={lockScheduling} />
                    )}
                    <LogisticsGroup
                      places={logistics}
                      trip={trip}
                      onNavigate={handleNavigate}
                      onRemove={handleRemove}
                      onSchedule={handleSchedule}
                      onUnschedule={handleUnschedule}
                      lockScheduling={lockScheduling}
                    />
                    {activities.length > 0 && (
                      <div className="border border-content-border rounded-xl overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 bg-content-focal/50 border-b border-content-border">
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-semibold uppercase tracking-wider text-secondary-emphasized">
                              Today's Itinerary
                            </span>
                            <p className="text-xs text-content-disabled truncate">
                              {activities[0]?.item.name}
                              {activities.length > 1 && `, and ${activities.length - 1} more`}
                            </p>
                          </div>
                          <span className="text-xs text-content-disabled shrink-0">
                            {activities.length}
                          </span>
                        </div>
                        {/* Same timeline visual as Curate — time column +
                            vertical line through pins. See TripPlanningView
                            for the offset rationale. */}
                        <div className="relative">
                          <div
                            className="absolute left-[5rem] top-[1.5rem] bottom-4 w-0.5 -translate-x-1/2 bg-secondary-emphasized/40 pointer-events-none z-10"
                            style={{
                              maskImage:
                                'linear-gradient(to bottom, black 0%, black calc(100% - 80px), transparent 100%)',
                              WebkitMaskImage:
                                'linear-gradient(to bottom, black 0%, black calc(100% - 80px), transparent 100%)',
                            }}
                          />
                          <div className="absolute left-[5rem] top-[1.5rem] w-2.5 h-2.5 rounded-full bg-white border-2 border-secondary-emphasized -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10" />
                          <div className="divide-y divide-content-divider">
                            {activities.map((tp, i) => {
                              const next = activities[i + 1];
                              const gap = next ? timelineGapMinutes(tp, next, activeDay) : null;
                              return (
                                <Fragment key={tp.item.id}>
                                  <TripPlaceRow
                                    tripPlace={tp}
                                    trip={trip}
                                    onNavigate={handleNavigate}
                                    onRemove={handleRemove}
                                    onSchedule={handleSchedule}
                                    onUnschedule={handleUnschedule}
                                    isCurrent={tp.item.id === currentActivityId}
                                    lockScheduling={lockScheduling}
                                    timeline={true}
                                    timelineDay={activeDay}
                                  />
                                  {gap != null && gap > 0 && <TimelineGapIndicator minutes={gap} />}
                                </Fragment>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── TripPanel ────────────────────────────────────────────────────────────────

export const TripPanel = ({ onClose, lockTripMeta, lockScheduling }: TripPanelProps) => {
  const { trips, selectedTripId } = useTripContext();

  const selectedTrip = trips.find((t) => t.id === selectedTripId) ?? null;

  if (!selectedTrip) return null;

  return (
    <div className="w-full h-full flex flex-col overflow-hidden border-l border-content-divider bg-white shadow-[-4px_0_12px_rgba(80,30,120,0.08)] md:w-[400px] md:shrink-0">
      <TripDetailView
        key={selectedTrip.id}
        trip={selectedTrip}
        onClose={onClose}
        lockTripMeta={lockTripMeta}
        lockScheduling={lockScheduling}
      />
    </div>
  );
};

// ─── TripPlanningView ─────────────────────────────────────────────────────────

export interface TripPlanningViewProps {
  trip: Trip;
  /** When provided, replaces the entire left (unscheduled) half of the layout.
   * The right (day-by-day schedule) half is unchanged. Used to render a place
   * detail in the same slot as the unscheduled list. */
  leftPaneOverride?: React.ReactNode;
}

const TripPlanningViewImpl = ({ trip, leftPaneOverride }: TripPlanningViewProps) => {
  const { removePlace, updatePlaceSchedule, unschedulePlace, updateTripDates } = useTripContext();

  const [currentLat] = useQueryState('lat', parseAsFloat);
  const [currentLng] = useQueryState('lng', parseAsFloat);
  const [, setLat] = useQueryState('lat', parseAsFloat);
  const [, setLng] = useQueryState('lng', parseAsFloat);
  const [, setRadius] = useQueryState('radius', parseAsInteger.withDefault(10));
  const [, setSelectedItemId] = useQueryState('item', parseAsString);

  const [activeDay, setActiveDay] = useActiveDay();
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editStartDate, setEditStartDate] = useState(trip.startDate);
  const [editEndDate, setEditEndDate] = useState(trip.endDate);
  // Mobile only — Unscheduled collapses so the day pager isn't pushed
  // way down. On md+ the list always shows (the CSS toggle is a no-op).
  const [isUnscheduledCollapsed, setIsUnscheduledCollapsed] = useState(false);

  // Day-pager scroll container — reset to top on day-change so the new
  // day's content always reads from the start. The actual scroll element
  // differs by breakpoint (outer wrapper on mobile, inner pane on md+),
  // so we hold a ref to each and scroll both — `scrollTo(0)` on a
  // non-scrolling element is a harmless no-op. Tracks the previous day
  // in a ref so the effect references `activeDay` (otherwise biome
  // strips it as an unused dep under --unsafe) and skips initial mount.
  const outerScrollRef = useRef<HTMLDivElement>(null);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const prevActiveDayRef = useRef(activeDay);
  useEffect(() => {
    if (prevActiveDayRef.current === activeDay) return;
    prevActiveDayRef.current = activeDay;
    outerScrollRef.current?.scrollTo({ top: 0 });
    timelineScrollRef.current?.scrollTo({ top: 0 });
  }, [activeDay]);

  const { weather } = useWeather(trip, currentLat, currentLng);
  const weatherByDate = new Map(weather.map((w) => [w.date, w]));
  const dayCount = tripDayCount(trip);
  const dateRange = formatDateRange(trip.startDate, trip.endDate);

  // See TripDetailView for rationale: activities require both day AND time
  // to count as scheduled; lodging/convenience just need a day.
  const isScheduled = (p: TripPlace): boolean => {
    if (p.scheduledDay == null) return false;
    if (p.placeType === 'activity') return p.scheduledTime != null;
    return true;
  };
  const unscheduled = trip.places.filter((p) => !isScheduled(p));
  const scheduled = trip.places.filter(isScheduled);
  const byDay = new Map<number, Array<TripPlace>>();
  for (let d = 1; d <= dayCount; d++) byDay.set(d, []);
  for (const p of scheduled) {
    if (p.scheduledDay == null) continue;
    const spansMultipleDays =
      p.scheduledDayEnd != null &&
      p.scheduledDayEnd !== p.scheduledDay &&
      (p.placeType === 'lodging' || p.placeType === 'convenience');
    if (spansMultipleDays && p.scheduledDayEnd != null) {
      for (let d = p.scheduledDay; d <= p.scheduledDayEnd; d++) byDay.get(d)?.push(p);
    } else {
      byDay.get(p.scheduledDay)?.push(p);
    }
  }

  const handleNavigate = useCallback(
    (item: ItineraryItem) => {
      void setLat(item.latitude);
      void setLng(item.longitude);
      void setRadius(5);
      void setSelectedItemId(item.id);
    },
    [setLat, setLng, setRadius, setSelectedItemId],
  );

  const handleRemove = useCallback(
    (placeId: string) => removePlace(trip.id, placeId),
    [removePlace, trip.id],
  );

  const handleSchedule = useCallback(
    (placeId: string, update: ScheduleFormSave) => updatePlaceSchedule(trip.id, placeId, update),
    [updatePlaceSchedule, trip.id],
  );

  const handleUnschedule = useCallback(
    (placeId: string) => unschedulePlace(trip.id, placeId),
    [unschedulePlace, trip.id],
  );

  const dayDirection = useDayChangeDirection(activeDay);

  return (
    // Curate is a two-pane workspace on desktop (trip header + unscheduled on
    // the left, day-by-day schedule on the right). On mobile the two panes
    // stack vertically and the page scrolls — splitting a phone screen in two
    // makes both halves unreadable.
    <div
      ref={outerScrollRef}
      className="flex-1 flex flex-col md:flex-row min-w-0 h-full overflow-y-auto md:overflow-hidden bg-white"
    >
      {/* Left: trip header + unscheduled — or override (e.g. place detail) */}
      <div className="w-full md:w-1/2 flex flex-col border-b md:border-b-0 md:border-r border-content-divider md:overflow-hidden">
        {leftPaneOverride !== undefined ? (
          leftPaneOverride
        ) : (
          <>
            {/* min-h here matches the sticky day-pager height on the
                right pane so the two panel headers align horizontally and
                their bottom borders meet at the vertical pane divider. */}
            <div className="shrink-0 px-4 py-3 border-b border-content-divider md:min-h-[6.5rem] md:flex md:flex-col md:justify-center">
              {isEditingDates ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-content-subtle">Edit trip dates</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-xs text-content-disabled block mb-0.5">Start</span>
                      <TextInput
                        type="date"
                        value={editStartDate}
                        onChange={(e) => {
                          setEditStartDate(e.target.value);
                          if (editEndDate < e.target.value) setEditEndDate(e.target.value);
                        }}
                      />
                    </div>
                    <div>
                      <span className="text-xs text-content-disabled block mb-0.5">End</span>
                      <TextInput
                        type="date"
                        value={editEndDate}
                        min={editStartDate}
                        onChange={(e) => setEditEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                  {(() => {
                    const newDayCount = Math.max(
                      1,
                      Math.round(
                        (new Date(`${editEndDate}T00:00:00`).getTime() -
                          new Date(`${editStartDate}T00:00:00`).getTime()) /
                          86_400_000,
                      ) + 1,
                    );
                    return (
                      trip.places.some(
                        (p) => p.scheduledDay != null && p.scheduledDay > newDayCount,
                      ) && (
                        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                          Some scheduled places fall outside the new date range and will be moved to
                          Unplanned.
                        </p>
                      )
                    );
                  })()}
                  <div className="flex gap-1.5">
                    <Button
                      fullWidth
                      className="flex-1"
                      onClick={() => {
                        updateTripDates(trip.id, editStartDate, editEndDate);
                        setActiveDay(1);
                        setIsEditingDates(false);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditStartDate(trip.startDate);
                        setEditEndDate(trip.endDate);
                        setIsEditingDates(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // Two-column layout: text on the left (flex-1 min-w-0),
                // photo corner pinned top-right. `items-start` anchors
                // the corner to the top so it aligns with the trip name
                // even when the meta block grows tall.
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <h2 className="font-diary text-2xl text-content-text leading-tight">
                      {trip.name}
                    </h2>
                    {/* Meta — date, location, place count flow inline when the
                        pane is wide enough and wrap each onto its own line
                        when it narrows. Each leg is `inline-flex` so its
                        icon stays glued to its text on every wrap point. */}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-content-subtle mt-1">
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <FontAwesomeIcon
                          icon={faCalendar}
                          style={{ width: 10, height: 10 }}
                          className="shrink-0"
                        />
                        <span>
                          {dateRange}
                          <span className="text-content-disabled"> · {formatDayCount(trip)}</span>
                        </span>
                      </span>
                      {trip.location && (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <FontAwesomeIcon
                            icon={faLocationDot}
                            style={{ width: 10, height: 10 }}
                            className="shrink-0"
                          />
                          <span className="truncate">{formatAddress(trip.location)}</span>
                        </span>
                      )}
                      <span className="text-content-disabled">
                        {trip.places.length} {trip.places.length === 1 ? 'place' : 'places'}
                      </span>
                    </div>
                    {/* Share/export/delete intentionally absent here — share
                      and export live in the trip selector menu; delete is
                      only available from the Explore-mode trip detail. */}
                  </div>
                  <TripPhotosCorner trip={trip} canEdit={true} />
                </div>
              )}
            </div>

            {/* Unscheduled list — collapsible on mobile (where this pane
                sits above the day pager and steals vertical room). On md+
                the header is non-interactive and the list always shows. */}
            <div className="flex-1 md:overflow-y-auto p-4">
              <button
                type="button"
                onClick={() => setIsUnscheduledCollapsed((v) => !v)}
                aria-expanded={!isUnscheduledCollapsed}
                className="w-full flex items-center gap-2 px-1 py-1.5 mb-1 text-left md:cursor-default md:pointer-events-none"
              >
                <FontAwesomeIcon
                  icon={isUnscheduledCollapsed ? faChevronRight : faChevronDown}
                  style={{ width: 9, height: 9 }}
                  className="md:hidden text-content-disabled shrink-0"
                />
                <span className="text-xs font-bold text-content-text uppercase tracking-wider">
                  Unscheduled
                </span>
                <span className="text-xs text-content-disabled">{unscheduled.length}</span>
              </button>
              <div className={twx(isUnscheduledCollapsed && 'hidden md:block')}>
                {unscheduled.length > 0 ? (
                  <div className="divide-y divide-content-divider border border-content-border rounded-xl overflow-hidden">
                    {unscheduled.map((tp) => (
                      <TripPlaceRow
                        key={tp.item.id}
                        tripPlace={tp}
                        trip={trip}
                        onNavigate={handleNavigate}
                        onRemove={handleRemove}
                        onSchedule={handleSchedule}
                        onUnschedule={handleUnschedule}
                      />
                    ))}
                  </div>
                ) : (
                  <InlineEmptyState
                    icon={faPartyBell}
                    title="All places are scheduled"
                    message="You can Scout for more places, or consider this the perfect trip!"
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right: day pager + day content */}
      <div className="flex-1 flex flex-col md:overflow-hidden">
        {/* Drop top padding on small screens — the stacked layout already
            has visual breathing room from the section above, and the
            extra `pt-4` made the sticky day pager feel detached. Keep
            it on md+ where the right column sits next to the left
            pane and needs the inset. */}
        {/* No top padding on the scroll container — the sticky pager's
            own py-3 is the right amount of breathing room, and any
            container pt would push the sticky element down by that
            amount (visible as a gap above the pager). */}
        <div ref={timelineScrollRef} className="flex-1 md:overflow-y-auto px-4 pb-4 pt-0">
          {dayCount > 0 ? (
            <>
              {/* Sticky day pager — see TripDetailView for the negative-mx
                  trick to extend the white background to the scroll-container
                  edges. `md:min-h-[6.5rem]` matches the left pane's
                  trip-header min-height above (search for the matching
                  value in TripPlanningView's left column) so the
                  bottom borders of the two panes meet cleanly at the
                  vertical divider. `z-20` keeps the sticky bg-white
                  above the day content's z-10 timeline spine + pins —
                  otherwise the later-in-DOM pins win the z-tie and
                  bleed through the pager. */}
              <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-4 bg-white grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-content-divider md:min-h-[6.5rem] [&>*:first-child]:justify-self-start [&>*:last-child]:justify-self-end">
                <ChipButton
                  tone="paging"
                  size="sm"
                  icon={faChevronLeft}
                  onClick={() => setActiveDay((d) => Math.max(1, d - 1))}
                  disabled={activeDay === 1}
                  aria-label="Previous day"
                  // min-w + justify-start keeps the prev button the
                  // same width whether or not "Day N-1" renders, so
                  // both pager buttons mirror each other and the day
                  // chip stays geometrically centered.
                  className="min-w-[88px] justify-start"
                >
                  {activeDay > 1 && <span>Day {activeDay - 1}</span>}
                </ChipButton>
                <div
                  key={`day-chip-${activeDay}`}
                  className="text-center inline-flex flex-col items-center bg-white border border-content-border rounded-2xl shadow-sm px-3 py-1.5 motion-safe:animate-kawaii-day-chip-tilt"
                >
                  <p className="text-xs font-bold text-content-text uppercase tracking-wider">
                    Day <TickCounter value={activeDay} /> of {dayCount}
                  </p>
                  <p className="text-xs text-content-subtle">
                    {formatDayLabel(dateForDay(trip, activeDay))}
                  </p>
                  {(() => {
                    const w = weatherByDate.get(dateForDay(trip, activeDay));
                    return w ? (
                      <p className="text-xs text-content-subtle flex items-center justify-center gap-1.5 mt-0.5">
                        <span
                          title={w.condition}
                          className="motion-safe:animate-kawaii-breath inline-block"
                        >
                          {w.conditionEmoji}
                        </span>
                        <span>
                          {w.tempHighF}°/{w.tempLowF}°
                        </span>
                        {w.precipitationProbability > 10 && (
                          <span className="text-info-text">💧{w.precipitationProbability}%</span>
                        )}
                      </p>
                    ) : null;
                  })()}
                </div>
                <ChipButton
                  tone="paging"
                  size="sm"
                  onClick={() => setActiveDay((d) => Math.min(dayCount, d + 1))}
                  disabled={activeDay === dayCount}
                  aria-label="Next day"
                  // Mirror the prev button's min-w + opposite justify so
                  // both pager buttons stay the same width even when
                  // "Day N+1" doesn't render (last day).
                  className="min-w-[88px] justify-end"
                >
                  {activeDay < dayCount && <span>Day {activeDay + 1}</span>}
                  <FontAwesomeIcon icon={faChevronRight} style={{ width: 11, height: 11 }} />
                </ChipButton>
              </div>

              {(() => {
                const places = byDay.get(activeDay) ?? [];
                const logistics = places.filter(
                  (p) => p.placeType === 'lodging' || p.placeType === 'convenience',
                );
                const activities = buildDayTimeline(places, activeDay);
                return (
                  <div
                    key={activeDay}
                    className={twx(
                      'motion-safe:animate-kawaii-day-fade',
                      dayDirection === 'next' && 'motion-safe:animate-kawaii-day-slide-next',
                      dayDirection === 'prev' && 'motion-safe:animate-kawaii-day-slide-prev',
                    )}
                  >
                    {/* Weather moved up into the pager center so it sits
                        directly beneath the day/date label as one group. */}
                    {activeDay === 1 && <EnRouteSection trip={trip} direction="in" />}
                    {activeDay === dayCount && dayCount > 1 && (
                      <EnRouteSection trip={trip} direction="out" />
                    )}
                    <LogisticsGroup
                      places={logistics}
                      trip={trip}
                      onNavigate={handleNavigate}
                      onRemove={handleRemove}
                      onSchedule={handleSchedule}
                      onUnschedule={handleUnschedule}
                    />
                    {activities.length > 0 ? (
                      <div className="border border-content-border rounded-xl overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 bg-content-focal/50 border-b border-content-border">
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-semibold uppercase tracking-wider text-secondary-emphasized">
                              Today's Itinerary
                            </span>
                            <p className="text-xs text-content-disabled truncate">
                              {activities[0]?.item.name}
                              {activities.length > 1 && `, and ${activities.length - 1} more`}
                            </p>
                          </div>
                          <span className="text-xs text-content-disabled shrink-0">
                            {activities.length}
                          </span>
                        </div>
                        <div className="relative">
                          {/* Vertical timeline. Line x: p-2.5 (0.625rem) +
                              time-col w-12 (3rem) + gap-2.5 (0.625rem) +
                              pin-half (0.75rem) = 5rem. Top end-cap at the
                              first pin's vertical center (p-2.5 0.625 +
                              mt-0.5 0.125 + pin-half 0.75 = 1.5rem) so the
                              pin (z-10) overlays it. Using rem keeps the
                              offsets scale with the base font size — theme.css
                              drops base to 15px under 1000px wide so absolute
                              px values would slide off-center. */}
                          <div
                            className="absolute left-[5rem] top-[1.5rem] bottom-4 w-0.5 -translate-x-1/2 bg-secondary-emphasized/40 pointer-events-none z-10"
                            style={{
                              maskImage:
                                'linear-gradient(to bottom, black 0%, black calc(100% - 80px), transparent 100%)',
                              WebkitMaskImage:
                                'linear-gradient(to bottom, black 0%, black calc(100% - 80px), transparent 100%)',
                            }}
                          />
                          <div className="absolute left-[5rem] top-[1.5rem] w-2.5 h-2.5 rounded-full bg-white border-2 border-secondary-emphasized -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10" />
                          <div className="divide-y divide-content-divider">
                            {activities.map((tp, i) => {
                              const next = activities[i + 1];
                              const gap = next ? timelineGapMinutes(tp, next, activeDay) : null;
                              return (
                                <Fragment key={tp.item.id}>
                                  <TripPlaceRow
                                    tripPlace={tp}
                                    trip={trip}
                                    onNavigate={handleNavigate}
                                    onRemove={handleRemove}
                                    onSchedule={handleSchedule}
                                    onUnschedule={handleUnschedule}
                                    timeline={true}
                                    timelineDay={activeDay}
                                  />
                                  {gap != null && gap > 0 && <TimelineGapIndicator minutes={gap} />}
                                </Fragment>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      logistics.length === 0 && (
                        <InlineEmptyState
                          size="sm"
                          icon={faCalendarPlus}
                          title="Nothing scheduled"
                          message="Use the calendar icon on any scouted place to curate it onto this day."
                        />
                      )
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <p className="text-xs text-content-disabled italic px-1">No days configured.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Memoize so AppInner re-renders for unrelated reasons (e.g., modal toggles
// or search refreshes) don't repaint the whole curate schedule.
export const TripPlanningView = memo(TripPlanningViewImpl);
